import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSubscriptionStatus, SUB_STATUS } from '../../hooks/useSubscriptionStatus';
import SubscriptionBanner from 'components/SubscriptionBanner';
import PrimaryNavBar from 'components/ui/PrimaryNavBar';
import Icon from 'components/AppIcon';
import ActiveAlertsPanel from './components/ActiveAlertsPanel';
import AlertRulesPanel from './components/AlertRulesPanel';
import AlertHistoryChart from './components/AlertHistoryChart';

// ─── Client-side alert evaluation ────────────────────────────────────────────

async function evaluateRules({ rules, batches, marketTicks, userId, supabaseClient }) {
  if (!rules?.length || !userId) return;

  const enabledRules = rules?.filter(r => r?.is_enabled);
  if (!enabledRules?.length) return;

  // Fetch existing active alerts to avoid duplicates
  const { data: existingAlerts } = await supabaseClient
    ?.from('alerts')
    ?.select('type, entity_id, status')
    ?.eq('user_id', userId)
    ?.eq('status', 'active');

  const existingSet = new Set(
    (existingAlerts || [])?.map(a => `${a?.type}::${a?.entity_id || 'global'}`)
  );

  const newAlerts = [];

  for (const rule of enabledRules) {
    const scopeKey = rule?.scope_metal_id || rule?.scope_category_id || 'global';
    const dedupeKey = `${rule?.type}::${scopeKey}`;
    if (existingSet?.has(dedupeKey)) continue;

    let triggered = false;
    let title = '';
    let description = '';
    let severity = 'INFO';
    let entityType = null;
    let entityId = null;

    if (rule?.type === 'INVENTORY_AGE') {
      const oldBatches = (batches || [])?.filter(b => {
        if (b?.status === 'sold') return false;
        const days = Math.floor((Date.now() - new Date(b?.created_at)) / 86400000);
        return days >= rule?.threshold_value;
      });
      if (oldBatches?.length > 0) {
        triggered = true;
        severity = 'ADVERTENCIA';
        title = `Inventario con antigüedad > ${rule?.threshold_value} días`;
        description = `${oldBatches?.length} lote(s) llevan más de ${rule?.threshold_value} días en inventario.`;
        entityType = 'inventory';
        entityId = `${oldBatches?.length}_batches`;
      }
    }

    if (rule?.type === 'BENCHMARK_SHOCK') {
      const recentTicks = (marketTicks || [])?.slice(0, 20);
      const ticksBySymbol = {};
      recentTicks?.forEach(t => {
        if (!ticksBySymbol?.[t?.symbol]) ticksBySymbol[t?.symbol] = [];
        ticksBySymbol?.[t?.symbol]?.push(t);
      });
      for (const [symbol, ticks] of Object.entries(ticksBySymbol)) {
        if (ticks?.length < 2) continue;
        const latest = parseFloat(ticks?.[0]?.price);
        const prev = parseFloat(ticks?.[1]?.price);
        if (prev > 0) {
          const changePct = Math.abs((latest - prev) / prev) * 100;
          if (changePct >= rule?.threshold_value) {
            triggered = true;
            severity = changePct >= rule?.threshold_value * 2 ? 'CRÍTICO' : 'ADVERTENCIA';
            title = `Shock de benchmark: ${symbol}`;
            description = `${symbol} cambió ${changePct?.toFixed(1)}% (umbral: ${rule?.threshold_value}%).`;
            entityType = 'benchmark';
            entityId = symbol;
            break;
          }
        }
      }
    }

    if (triggered) {
      newAlerts?.push({
        user_id: userId,
        type: rule?.type,
        severity,
        title,
        description,
        entity_type: entityType,
        entity_id: entityId,
        status: 'active',
        triggered_at: new Date()?.toISOString(),
      });
    }
  }

  if (newAlerts?.length > 0) {
    await supabaseClient?.from('alerts')?.insert(newAlerts);
    // Update last_triggered_at for fired rules
    const firedTypes = [...new Set(newAlerts?.map(a => a?.type))];
    for (const type of firedTypes) {
      await supabaseClient
        ?.from('alert_rules')
        ?.update({ last_triggered_at: new Date()?.toISOString() })
        ?.eq('user_id', userId)
        ?.eq('type', type);
    }
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AlertsDashboard() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [metals, setMetals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [batches, setBatches] = useState([]);
  const [marketTicks, setMarketTicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [newAlertIds, setNewAlertIds] = useState(new Set());
  const tickChannelRef = useRef(null);

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [alertsRes, rulesRes, metalsRes, catsRes, batchesRes, ticksRes] = await Promise.all([
        supabase?.from('alerts')?.select('*')?.eq('user_id', user?.id)?.order('triggered_at', { ascending: false }),
        supabase?.from('alert_rules')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false }),
        supabase?.from('metals')?.select('id, name')?.eq('user_id', user?.id),
        supabase?.from('scrap_categories')?.select('id, name')?.eq('user_id', user?.id),
        supabase?.from('batches')?.select('id, created_at, status, metal_id')?.eq('user_id', user?.id),
        supabase?.from('market_ticks')?.select('symbol, price, recorded_at')?.eq('user_id', user?.id)?.order('recorded_at', { ascending: false })?.limit(50),
      ]);

      if (!alertsRes?.error) setAlerts(alertsRes?.data || []);
      if (!rulesRes?.error) setRules(rulesRes?.data || []);
      if (!metalsRes?.error) setMetals(metalsRes?.data || []);
      if (!catsRes?.error) setCategories(catsRes?.data || []);
      if (!batchesRes?.error) setBatches(batchesRes?.data || []);
      if (!ticksRes?.error) setMarketTicks(ticksRes?.data || []);
      setLastSync(new Date());
    } catch (err) {
      console.error('Error loading alerts data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Evaluate rules after data loads
  useEffect(() => {
    if (!loading && rules?.length > 0 && user?.id) {
      evaluateRules({ rules, batches, marketTicks, userId: user?.id, supabaseClient: supabase });
    }
  }, [loading, rules, batches, marketTicks, user?.id]);

  // Real-time subscription on alerts table using useSubscriptionStatus
  const { status: alertSubStatus, subWarningBanner, retryNow } = useSubscriptionStatus({
    channelName: `alerts_rt_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'alerts',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: (payload) => {
      if (payload?.eventType === 'INSERT') {
        const newAlert = payload?.new;
        setAlerts(prev => [newAlert, ...prev]);
        setNewAlertIds(prev => new Set([...prev, newAlert?.id]));
        setTimeout(() => {
          setNewAlertIds(prev => {
            const next = new Set(prev);
            next?.delete(newAlert?.id);
            return next;
          });
        }, 4000);
      } else if (payload?.eventType === 'UPDATE') {
        setAlerts(prev => prev?.map(a => a?.id === payload?.new?.id ? payload?.new : a));
      } else if (payload?.eventType === 'DELETE') {
        setAlerts(prev => prev?.filter(a => a?.id !== payload?.old?.id));
      }
    },
    onReconnect: loadData,
  });

  useEffect(() => {
    if (!user?.id) return;
    loadData();

    // Real-time on market_ticks to re-evaluate rules
    tickChannelRef.current = supabase
      ?.channel(`ticks_alerts_${user?.id}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'market_ticks',
        filter: `user_id=eq.${user?.id}`,
      }, async () => {
        const { data } = await supabase
          ?.from('market_ticks')
          ?.select('symbol, price, recorded_at')
          ?.eq('user_id', user?.id)
          ?.order('recorded_at', { ascending: false })
          ?.limit(50);
        if (data) {
          setMarketTicks(data);
          evaluateRules({ rules, batches, marketTicks: data, userId: user?.id, supabaseClient: supabase });
        }
      })
      ?.subscribe();

    return () => {
      if (tickChannelRef?.current) supabase?.removeChannel(tickChannelRef?.current);
    };
  }, [user?.id]);

  // ─── Alert actions ──────────────────────────────────────────────────────────

  const handleAcknowledge = useCallback(async (alertId) => {
    const { error } = await supabase
      ?.from('alerts')
      ?.update({ status: 'acknowledged', acknowledged_at: new Date()?.toISOString() })
      ?.eq('id', alertId)
      ?.eq('user_id', user?.id);
    if (!error) {
      setAlerts(prev => prev?.map(a => a?.id === alertId ? { ...a, status: 'acknowledged', acknowledged_at: new Date()?.toISOString() } : a));
    }
  }, [user?.id]);

  const handleResolve = useCallback(async (alertId, note) => {
    const { error } = await supabase
      ?.from('alerts')
      ?.update({ status: 'resolved', resolved_at: new Date()?.toISOString(), resolution_note: note || null })
      ?.eq('id', alertId)
      ?.eq('user_id', user?.id);
    if (!error) {
      setAlerts(prev => prev?.map(a => a?.id === alertId ? { ...a, status: 'resolved', resolved_at: new Date()?.toISOString(), resolution_note: note || null } : a));
    }
  }, [user?.id]);

  const handleBulkAcknowledge = useCallback(async (alertIds) => {
    const now = new Date()?.toISOString();
    const { error } = await supabase
      ?.from('alerts')
      ?.update({ status: 'acknowledged', acknowledged_at: now })
      ?.in('id', alertIds)
      ?.eq('user_id', user?.id);
    if (!error) {
      setAlerts(prev => prev?.map(a => alertIds?.includes(a?.id) ? { ...a, status: 'acknowledged', acknowledged_at: now } : a));
    }
  }, [user?.id]);

  // ─── Rule actions ────────────────────────────────────────────────────────────

  const handleToggleRule = useCallback(async (ruleId, enabled) => {
    const { error } = await supabase
      ?.from('alert_rules')
      ?.update({ is_enabled: enabled })
      ?.eq('id', ruleId)
      ?.eq('user_id', user?.id);
    if (!error) {
      setRules(prev => prev?.map(r => r?.id === ruleId ? { ...r, is_enabled: enabled } : r));
    }
  }, [user?.id]);

  const handleCreateRule = useCallback(async (data) => {
    const { data: created, error } = await supabase
      ?.from('alert_rules')
      ?.insert({ ...data, user_id: user?.id })
      ?.select()
      ?.single();
    if (!error && created) {
      setRules(prev => [created, ...prev]);
    }
  }, [user?.id]);

  const handleEditRule = useCallback(async (ruleId, data) => {
    const { data: updated, error } = await supabase
      ?.from('alert_rules')
      ?.update(data)
      ?.eq('id', ruleId)
      ?.eq('user_id', user?.id)
      ?.select()
      ?.single();
    if (!error && updated) {
      setRules(prev => prev?.map(r => r?.id === ruleId ? updated : r));
    }
  }, [user?.id]);

  const handleDeleteRule = useCallback(async (ruleId) => {
    const { error } = await supabase
      ?.from('alert_rules')
      ?.delete()
      ?.eq('id', ruleId)
      ?.eq('user_id', user?.id);
    if (!error) {
      setRules(prev => prev?.filter(r => r?.id !== ruleId));
    }
  }, [user?.id]);

  const activeCount = alerts?.filter(a => a?.status === 'active')?.length;
  const criticalCount = alerts?.filter(a => a?.severity === 'CRÍTICO' && a?.status === 'active')?.length;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryNavBar />
      <main className="pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-6 py-6">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div>
              <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Link to="/main-dashboard" className="hover:text-foreground transition-colors">Dashboard</Link>
                <Icon name="ChevronRight" size={12} color="currentColor" />
                <span className="text-foreground font-medium">Alertas</span>
              </nav>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-foreground">Panel de Alertas</h1>
                {criticalCount > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 animate-pulse">
                    <Icon name="AlertCircle" size={12} color="#b91c1c" />
                    {criticalCount} crítica{criticalCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">Monitoreo en tiempo real de alertas y reglas de operación</p>
            </div>
            <div className="flex items-center gap-3">
              {lastSync && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    alertSubStatus === SUB_STATUS?.CONNECTED ? 'bg-emerald-500 animate-pulse' :
                    alertSubStatus === SUB_STATUS?.FAILED ? 'bg-red-500' : 'bg-amber-400 animate-pulse'
                  }`} />
                  {alertSubStatus === SUB_STATUS?.CONNECTED ? 'EN VIVO' : 'Actualizado'} {lastSync?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80 transition-colors"
              >
                <Icon name="RefreshCw" size={14} color="currentColor" />
                Sincronizar
              </button>
            </div>
          </div>

          <SubscriptionBanner status={alertSubStatus} subWarningBanner={subWarningBanner} retryNow={retryNow} />

          {/* Summary KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Alertas activas', value: activeCount, icon: 'Bell', color: activeCount > 0 ? 'text-red-600' : 'text-emerald-600', bg: activeCount > 0 ? 'bg-red-50' : 'bg-emerald-50' },
              { label: 'Críticas', value: criticalCount, icon: 'AlertCircle', color: 'text-red-600', bg: 'bg-red-50' },
              { label: 'Reglas activas', value: rules?.filter(r => r?.is_enabled)?.length, icon: 'Settings2', color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Resueltas hoy', value: alerts?.filter(a => a?.status === 'resolved' && a?.resolved_at && new Date(a?.resolved_at)?.toDateString() === new Date()?.toDateString())?.length, icon: 'CheckCircle', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            ]?.map((kpi, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">{kpi?.label}</span>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${kpi?.bg}`}>
                    <Icon name={kpi?.icon} size={14} color={kpi?.color?.replace('text-', '')} strokeWidth={2} />
                  </div>
                </div>
                {loading ? (
                  <div className="h-7 w-12 bg-muted rounded animate-pulse" />
                ) : (
                  <p className={`text-2xl font-bold font-mono ${kpi?.color}`}>{kpi?.value}</p>
                )}
              </div>
            ))}
          </div>

          {/* Active Alerts + History Chart */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            <div className="xl:col-span-2" data-tour="alerts-active">
              <ActiveAlertsPanel
                alerts={alerts}
                loading={loading}
                onAcknowledge={handleAcknowledge}
                onResolve={handleResolve}
                onBulkAcknowledge={handleBulkAcknowledge}
                newAlertIds={newAlertIds}
              />
            </div>
            <div>
              <AlertHistoryChart alerts={alerts} loading={loading} />
            </div>
          </div>

          {/* Alert Rules */}
          <div data-tour="alerts-rules">
            <AlertRulesPanel
              rules={rules}
              metals={metals}
              categories={categories}
              loading={loading}
              onToggle={handleToggleRule}
              onCreate={handleCreateRule}
              onEdit={handleEditRule}
              onDelete={handleDeleteRule}
            />
          </div>

        </div>
      </main>
    </div>
  );
}
